import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, Award, Users, Heart, Shield, BookOpen, Target } from 'lucide-react';
import { nurseInfo, aboutInfo } from '../data/mock';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Heart size={16} className="text-red-500" />
            <span>À propos de PodioSoins+</span>
          </div>
          
          <h1 className="heading-hero mb-4">
            Rencontrez {nurseInfo.name}
          </h1>
          
          <p className="body-large text-gray-700 max-w-2xl mx-auto">
            {nurseInfo.title} passionnée par les soins de qualité et le bien-être de ses patients.
          </p>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Photo and Quick Stats */}
            <div className="space-y-8">
              <div className="relative">
                <img 
                  src={nurseInfo.image} 
                  alt={`${nurseInfo.name}, Infirmière spécialisée`}
                  className="rounded-xl shadow-lg hover-lift w-full h-96 object-cover"
                />
                
                {/* Stats Overlay */}
                <div className="absolute -bottom-6 left-6 right-6">
                  <div className="flex justify-center">
                    <div className="service-card accent-blue text-center">
                      <Award size={24} className="text-blue-600 mx-auto mb-2" />
                      <p className="body-small font-medium">10+</p>
                      <p className="caption">Années d'expérience</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact Card */}
              <div className="service-card accent-cream">
                <h3 className="heading-3 mb-4">Contactez-moi</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone size={16} className="text-blue-600" />
                    <span className="body-small">{nurseInfo.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Heart size={16} className="text-red-500" />
                    <span className="body-small">{nurseInfo.email}</span>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Link to="/contact" className="btn-primary flex-1 justify-center hover-scale">
                    <Calendar size={16} className="mr-2" />
                    <span className="button-text">Rendez-vous</span>
                  </Link>
                  <a href={`tel:${nurseInfo.phone}`} className="btn-secondary flex-1 justify-center hover-scale">
                    <Phone size={16} className="mr-2" />
                    <span className="button-text">Appeler</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              {/* Introduction */}
              <div className="space-y-4">
                <h2 className="heading-1">Mon parcours</h2>
                <p className="body-large text-gray-700 leading-relaxed">
                  {aboutInfo.introduction}
                </p>
              </div>

              {/* Qualifications */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <BookOpen size={24} className="text-blue-600" />
                  <h3 className="heading-2">Qualifications et certifications</h3>
                </div>
                
                <div className="space-y-3">
                  {aboutInfo.qualifications.map((qualification, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Shield size={16} className="text-green-600 mt-1 flex-shrink-0" />
                      <p className="body-medium text-gray-700">{qualification}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Approach */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <Heart size={24} className="text-red-500" />
                  <h3 className="heading-2">Mon approche</h3>
                </div>
                <p className="body-medium text-gray-700 leading-relaxed">
                  {aboutInfo.approach}
                </p>
              </div>

              {/* Mission */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <Target size={24} className="text-purple-600" />
                  <h3 className="heading-2">Ma mission</h3>
                </div>
                <p className="body-medium text-gray-700 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specializations Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Mes spécialisations</h2>
            <p className="body-large text-gray-600 max-w-2xl mx-auto">
              Je me concentre sur deux domaines essentiels pour votre bien-être et votre santé.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="service-card accent-blue hover-lift">
              <div className="flex items-center space-x-3 mb-4">
                <Shield size={32} className="text-blue-600" />
                <h3 className="heading-2">Soins de plaies</h3>
              </div>
              <p className="body-medium text-gray-700 mb-4">
                Spécialisée dans l'évaluation, le traitement et le suivi des plaies de tous types. 
                Mon expertise permet une guérison optimale et la prévention des complications.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="body-small">Plaies chroniques et aigus</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="body-small">Prévention des infections</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="body-small">Pansements spécialisés avec prescription</span>
                </li>
              </ul>
            </div>
            
            <div className="service-card accent-green hover-lift">
              <div className="flex items-center space-x-3 mb-4">
                <Heart size={32} className="text-green-600" />
                <h3 className="heading-2">Soins de pieds</h3>
              </div>
              <p className="body-medium text-gray-700 mb-4">
                Experte en soins podologiques, je traite les affections courantes des pieds 
                avec une attention particulière aux patients diabétiques.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="body-small">Ongles incarnés et mycoses</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="body-small">Cors et callosités</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="body-small">Soins pour pieds diabétiques</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">Mes valeurs</h2>
            <p className="body-large text-gray-600 max-w-2xl mx-auto">
              Ce qui guide ma pratique quotidienne et mes relations avec les patients.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="service-card accent-cream mx-auto w-20 h-20 rounded-full flex items-center justify-center">
                <Heart size={32} className="text-red-500" />
              </div>
              <h3 className="heading-3">Compassion</h3>
              <p className="body-medium text-gray-600">
                Chaque patient mérite d'être traité avec empathie, respect et dignité.
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="service-card accent-blue mx-auto w-20 h-20 rounded-full flex items-center justify-center">
                <Shield size={32} className="text-blue-600" />
              </div>
              <h3 className="heading-3">Excellence</h3>
              <p className="body-medium text-gray-600">
                Je m'engage à fournir des soins de la plus haute qualité basés sur les meilleures pratiques.
              </p>
            </div>
            
            <div className="text-center space-y-4">
              <div className="service-card accent-green mx-auto w-20 h-20 rounded-full flex items-center justify-center">
                <Users size={32} className="text-green-600" />
              </div>
              <h3 className="heading-3">Proximité</h3>
              <p className="body-medium text-gray-600">
                Des soins personnalisés à domicile pour votre confort et votre bien-être.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="container text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="heading-1">Prêt à faire connaissance ?</h2>
            <p className="body-large text-gray-700">
              Je serais ravie de discuter de vos besoins en soins de santé et de voir 
              comment je peux vous aider.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact" className="btn-primary hover-scale">
                <Calendar size={16} className="mr-2" />
                <span className="button-text">Prendre rendez-vous</span>
              </Link>
              <Link to="/services" className="btn-secondary hover-scale">
                <BookOpen size={16} className="mr-2" />
                <span className="button-text">Voir mes services</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;